import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnChanges,
  SimpleChanges,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  PartnerBankJourneyCardResponse,
  PartnerBankJourneyCommentResponse,
  partnerBankJourneyStageLabel,
} from '@interfaces/partners/responses/partner-bank-journey.response';
import { Permissions } from '@guard/Permissions';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { mensagemDoErro } from '@features/dashboard/meetings/components/meeting-presenter';
import { DrawerModule } from 'primeng/drawer';
import { Subscription } from 'rxjs';

/** Limite do texto do comentário, o mesmo do backend. */
const COMMENT_MAX_LENGTH = 2000;

/**
 * Comentários de um banco da **Jornada nos bancos** (card 3612), num painel lateral.
 *
 * `p-drawer` do PrimeNG direto porque o Design System Vault não tem wrapper de painel lateral (os filtros
 * do projeto usam o mesmo `p-drawer`). Lista os comentários em ordem cronológica.
 *
 * Card 3848: os comentários são os da negociação no **Pipeline de Crédito** (comentou lá, aparece aqui, e o
 * contrário), e só o time interno comenta. O campo aparece quando o cartão vem com `canComment` (usuário interno com
 * `PIPELINE_DEAL_COMMENT_CREATE`, resolvido no servidor) e a permissão está no token; o parceiro só lê. Recusa do
 * backend aparece em toast com a mensagem dele.
 */
@Component({
  selector: 'app-bank-journey-comments-drawer',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, DrawerModule],
  templateUrl: './bank-journey-comments-drawer.component.html',
  styleUrl: './bank-journey-comments-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BankJourneyCommentsDrawerComponent implements OnChanges {
  /** Banco aberto; `null` fecha o painel. */
  readonly card = input<PartnerBankJourneyCardResponse | null>(null);
  readonly closed = output<void>();
  /** Um comentário foi gravado (a jornada relê o contador). */
  readonly commented = output<void>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly partnersService = inject(PartnersHttpService);
  private readonly formErrorHandler = inject(FormErrorHandlerService);

  protected readonly comments = signal<PartnerBankJourneyCommentResponse[]>([]);
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly text = signal('');
  protected readonly sending = signal(false);
  protected readonly maxLength = COMMENT_MAX_LENGTH;
  protected readonly stageLabel = partnerBankJourneyStageLabel;

  /** Defesa junto do `canComment` do servidor: a mesma permissão que o POST confere (a de comentar no pipe). */
  protected readonly hasCommentPermission = toSignal(
    inject(PermissionsService).hasPermission(Permissions.Pipeline.DEAL_COMMENT_CREATE),
    { initialValue: false }
  );

  /** Mostra o campo de comentar: o servidor liberou e a permissão está no token. */
  protected canWrite(card: PartnerBankJourneyCardResponse): boolean {
    return card.canComment && this.hasCommentPermission();
  }

  private loadedCardId: string | null = null;
  private loadedCommentCount: number | null = null;
  private request?: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['card']) return;

    // A releitura do tempo real troca o objeto do mesmo banco: recarrega quando o banco muda ou quando o
    // contador de comentários mudou (alguém comentou com o painel aberto).
    const cardId = this.card()?.pipelineDealId ?? null;
    const commentCount = this.card()?.commentCount ?? null;
    if (cardId === this.loadedCardId) {
      if (cardId && commentCount !== this.loadedCommentCount) {
        this.loadedCommentCount = commentCount;
        this.load();
      }
      return;
    }

    this.loadedCardId = cardId;
    this.loadedCommentCount = commentCount;
    this.comments.set([]);
    this.text.set('');
    this.error.set(null);
    if (cardId) this.load();
  }

  /** Carrega os comentários do banco aberto; também é o "Tentar de novo". */
  protected load(): void {
    const cardId = this.loadedCardId;
    if (!cardId) return;

    this.request?.unsubscribe();
    this.loading.set(true);
    this.error.set(null);

    this.request = this.partnersService
      .getBankJourneyComments(cardId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: comments => {
          this.loading.set(false);
          this.comments.set(comments);
        },
        error: err => {
          this.loading.set(false);
          this.error.set(
            mensagemDoErro(
              err,
              'Não foi possível carregar os comentários deste banco. Use Tentar de novo; se continuar, avise o time Legatus.'
            )
          );
        },
      });
  }

  protected canSend(): boolean {
    const text = this.text().trim();
    return !this.sending() && text.length > 0 && text.length <= COMMENT_MAX_LENGTH;
  }

  protected send(): void {
    const cardId = this.loadedCardId;
    if (!cardId || !this.canSend()) return;

    this.sending.set(true);
    this.partnersService
      .addBankJourneyComment(cardId, this.text().trim())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: comment => {
          this.sending.set(false);
          if (comment) this.comments.update(current => [...current, comment]);
          this.text.set('');
          this.commented.emit();
        },
        error: err => {
          this.sending.set(false);
          this.formErrorHandler.showToast(
            err,
            'O comentário não foi gravado. O texto continua no campo; tente de novo.'
          );
        },
      });
  }

  protected onVisibleChange(visible: boolean): void {
    if (!visible) this.closed.emit();
  }
}
